import React, { useState, useCallback } from 'react';
import { CheckCircle, AlertTriangle, X } from 'lucide-react';
import { useVessel } from '../../../../contexts/VesselContext';
import { useCrew } from '../../../../hooks/useCrew';
import { useFleetCrew } from '../../../../hooks/domain/fleet/useFleetCrew';  // FT-05
import { usePermissions } from '../../../../hooks/usePermissions';
import { useMainLogbook } from '../../../../hooks/domain/operations/useMainLogbook';
import AddFromOrgModal from '../../crew/modals/AddFromOrgModal';  // FT-05
// FT-357: shell-level back handler — register a "back to dashboard" handler
// so mobile users always see a Back button in the bottom bar.
import { useSetBackHandler } from '../../../../contexts/NavigationBackContext';

// Child components (already exist - don't touch them)
import CrewListContent from './CrewListContent';
import CrewProfileView from './CrewProfileView';
import AddCrewView from './AddCrewView';
import FamiliarisationContent from './FamiliarisationContent';
import HoursOfRestModule from '../hours/HoursOfRestModule';
import TabNavigation from '../../TabNavigation';
import CrewSettingsView from './CrewSettingsView';

// Standard tab configuration
const tabs = [
  { id: 'list', label: 'Crew List' },
  { id: 'hours', label: 'Hours of Rest' },
  { id: 'familiarisation', label: 'Familiarisation' }
];

/**
 * Crew Module - Rebuilt 2025-12-10
 * Phase 2: Tabs & Navigation complete
 * Following HoursOfRestModule pattern for stability
 * Zero infinite loops - simple 2-layer hook architecture
 */
const CrewModule = ({ setActiveModule }) => {
  // 1. HOOKS (stable order)
  const { currentVesselId } = useVessel();
  const { crew, addCrew, updateCrew, deleteCrew, archiveCrew, restoreCrew } = useCrew();
  const { can } = usePermissions();
  const { logCrewChange } = useMainLogbook();

  // FT-357: register a shell-level "back to dashboard" handler so mobile
  // users always see a Back button in the bottom bar for this module.
  // Nested drill-down views (profile, add, edit, familiarisation, settings)
  // register their own handlers on top of this via useSetBackHandler and pop
  // back to this handler when they unmount.
  useSetBackHandler(
    useCallback(() => {
      if (setActiveModule) setActiveModule('dashboard');
    }, [setActiveModule])
  );

  // 2. STATE (minimal - only UI state)
  const [activeTab, setActiveTab] = useState('list');
  const [selectedCrew, setSelectedCrew] = useState(null);
  const [feedbackMessage, setFeedbackMessage] = useState(null);
  const [showAddFromOrgModal, setShowAddFromOrgModal] = useState(false);  // FT-05

  // 3. CALLBACKS (all memoized for stability)

  // Select crew member and navigate to profile
  const handleSelectCrew = useCallback((crewMember) => {
    setSelectedCrew(crewMember);
    setActiveTab('profile');
  }, []);

  // Back to list from any view
  const handleBackToList = useCallback(() => {
    setSelectedCrew(null);
    setActiveTab('list');
  }, []);

  // Navigate to add crew form
  const handleAddCrew = useCallback(() => {
    setSelectedCrew(null); // Clear selection
    setActiveTab('add');
  }, []);

  // Navigate to edit crew form (from profile)
  const handleEditCrew = useCallback((crewMember) => {
    setSelectedCrew(crewMember);
    setActiveTab('edit');
  }, []);

  // Cancel from add/edit form
  const handleCancelForm = useCallback(() => {
    setSelectedCrew(null);
    setActiveTab('list');
  }, []);

  // Save crew (add or edit)
  const handleSaveCrew = useCallback(async (crewData) => {
    // RBAC check - use action/resource pattern
    const isEdit = !!selectedCrew;
    const action = isEdit ? 'update' : 'create';
    if (!can(action, 'crew')) {

      setFeedbackMessage({
        type: 'error',
        message: 'You do not have permission to manage crew members.'
      });
      return null;
    }

    try {
      let result;
      if (isEdit) {
        // Editing existing crew
        result = await updateCrew(selectedCrew.id, crewData);
      } else {
        // Adding new crew
        result = await addCrew(crewData);
      }

      if (result) {
        // Log new crew addition to Official Logbook (not edits)
        if (!isEdit) {
          await logCrewChange({
            timestamp: new Date().toISOString(),
            signOns: [{
              id: result.id,
              name: crewData.name,
              role: crewData.role || crewData.position
            }],
            signOffs: [],
            notes: `${crewData.name} joined vessel as ${crewData.role || crewData.position}`
          });
        }

        // Success - show feedback
        setFeedbackMessage({
          type: 'success',
          message: isEdit ? 'Crew member updated successfully' : 'Crew member added successfully'
        });

        // Clear feedback after 3 seconds
        setTimeout(() => setFeedbackMessage(null), 3000);

        // Clear form and return to list
        setSelectedCrew(null);
        setActiveTab('list');
        return result;
      } else {
        // Save failed - show error
        setFeedbackMessage({
          type: 'error',
          message: isEdit
            ? 'Failed to update crew member. Please check your connection and try again.'
            : 'Failed to add crew member. Check for duplicate names or try again.'
        });
        return null;
      }
    } catch (error) {

      setFeedbackMessage({
        type: 'error',
        message: error.message || 'An unexpected error occurred. Please try again.'
      });
      return null;
    }
  }, [addCrew, updateCrew, can, selectedCrew, logCrewChange]);

  // Navigate to familiarisation tab
  const handleFamiliarisation = useCallback(() => {
    setActiveTab('familiarisation');
  }, []);

  // Handle crew deletion
  const handleDeleteCrew = useCallback(async (crewId) => {
    // RBAC check
    if (!can('delete', 'crew')) {

      return false;
    }

    const result = await deleteCrew(crewId);
    if (result) {
      // If deleted crew was selected, clear selection
      if (selectedCrew?.id === crewId) {
        setSelectedCrew(null);
        setActiveTab('list');
      }
    }
    return result;
  }, [deleteCrew, can, selectedCrew]);

  // Handle crew archive (sign-off)
  const handleArchiveCrew = useCallback(async (crewId) => {
    // RBAC check
    if (!can('update', 'crew')) {

      return false;
    }

    // Find the crew member before archiving
    const crewMember = crew.find(c => c.id === crewId);

    const result = await archiveCrew(crewId);
    if (result && crewMember) {
      // Log crew sign-off to Official Logbook
      await logCrewChange({
        timestamp: new Date().toISOString(),
        signOns: [],
        signOffs: [{
          id: crewMember.id,
          name: crewMember.name,
          role: crewMember.role || crewMember.position
        }],
        notes: `${crewMember.name} signed off vessel`
      });

      // If archived crew was selected, go back to list
      if (selectedCrew?.id === crewId) {
        setSelectedCrew(null);
        setActiveTab('list');
      }
    }
    return result;
  }, [archiveCrew, can, selectedCrew, crew, logCrewChange]);

  // Navigate to settings
  const handleOpenSettings = useCallback(() => {
    setActiveTab('settings');
  }, []);

  // FT-05: Open Add from Organisation modal
  const handleOpenAddFromOrg = useCallback(() => {
    setShowAddFromOrgModal(true);
  }, []);

  // FT-05: Handle crew assigned from org
  const handleAssignFromOrg = useCallback((crewMember) => {
    setFeedbackMessage({
      type: 'success',
      message: `${crewMember.name} assigned to this vessel`
    });
    setTimeout(() => setFeedbackMessage(null), 3000);
  }, []);

  // 4. EARLY RETURNS FOR VIEW SWITCHING (HoursOfRestModule pattern)

  // Settings view (full-screen)
  if (activeTab === 'settings') {
    return <CrewSettingsView onBack={handleBackToList} />;
  }

  // Profile view
  if (activeTab === 'profile' && selectedCrew) {
    return (
      <CrewProfileView
        crew={selectedCrew}
        onBack={handleBackToList}
        onEdit={handleEditCrew}
        onDelete={handleDeleteCrew}
        onArchive={handleArchiveCrew}
      />
    );
  }

  // Edit crew view (from profile)
  if (activeTab === 'edit' && selectedCrew) {
    return (
      <AddCrewView
        initialData={selectedCrew}
        existingCrew={crew}
        onSave={handleSaveCrew}
        onBack={handleCancelForm}
      />
    );
  }

  // Add new crew view
  if (activeTab === 'add') {
    return (
      <AddCrewView
        existingCrew={crew}
        onSave={handleSaveCrew}
        onBack={handleCancelForm}
      />
    );
  }

  // Familiarisation view
  if (activeTab === 'familiarisation') {
    return (
      <>
        {/* Module Title */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Crew</h1>
          <p className="mt-1 text-sm text-gray-500">Manage crew members, hours of rest, and familiarisation</p>
        </div>

        <TabNavigation tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />

        <div className="mt-6">
          <FamiliarisationContent onBack={handleBackToList} />
        </div>
      </>
    );
  }

  // Hours of rest view
  if (activeTab === 'hours') {
    return (
      <>
        {/* Module Title */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Crew</h1>
          <p className="mt-1 text-sm text-gray-500">Manage crew members, hours of rest, and familiarisation</p>
        </div>

        <TabNavigation tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />

        <div className="mt-6">
          <HoursOfRestModule />
        </div>
      </>
    );
  }

  // 5. DEFAULT VIEW: List
  return (
    <>
      {/* Feedback Message */}
      {feedbackMessage && (
        <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg ${
          feedbackMessage.type === 'success'
            ? 'bg-green-100 border border-green-400 text-green-700'
            : 'bg-red-100 border border-red-400 text-red-700'
        }`}>
          <div className="flex items-center gap-2">
            {feedbackMessage.type === 'success' ? (
              <CheckCircle className="h-5 w-5" />
            ) : (
              <AlertTriangle className="h-5 w-5" />
            )}
            <span className="font-medium">{feedbackMessage.message}</span>
            <button
              onClick={() => setFeedbackMessage(null)}
              className="ml-4 text-gray-500 hover:text-gray-700"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Module Title */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Crew</h1>
        <p className="mt-1 text-sm text-gray-500">Manage crew members, hours of rest, and familiarisation</p>
      </div>

      {/* Standard Tab Navigation */}
      <TabNavigation tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />

      {/* Tab Content */}
      <div className="mt-6">
        <CrewListContent
          crew={crew}
          onViewCrew={handleSelectCrew}
          onViewProfile={handleSelectCrew}
          onAddCrew={handleAddCrew}
          onAddFromOrg={handleOpenAddFromOrg}  // FT-05
          onDeleteCrew={handleDeleteCrew}
          onArchiveCrew={handleArchiveCrew}
          onFamiliarisation={handleFamiliarisation}
          onOpenSettings={handleOpenSettings}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
        />
      </div>

      {/* FT-05: Add from Organisation Modal */}
      <AddFromOrgModal
        isOpen={showAddFromOrgModal}
        onClose={() => setShowAddFromOrgModal(false)}
        onAssign={handleAssignFromOrg}
      />
    </>
  );
};

export default CrewModule;
